import { useEffect } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

export default function HomeHealthAide() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://celebrity-homehealth.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    '@id': `${siteUrl}/services/home-health-aide#webpage`,
    url: `${siteUrl}/services/home-health-aide`,
    name: 'Home Health Aide Services Las Vegas | Celebrity Home Health',
    description: 'Compassionate home health aide services in Las Vegas — bathing, dressing, grooming, meal preparation, medication reminders, and companionship. Medicare certified.',
    isPartOf: { '@id': `${siteUrl}/#website` },
    medicalSpecialty: 'Geriatric',
    lastReviewed: '2026-04-11',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/services` },
        { '@type': 'ListItem', position: 3, name: 'Home Health Aide', item: `${siteUrl}/services/home-health-aide` },
      ],
    },
  };

  useEffect(() => {
    document.title = 'Home Health Aide Services Las Vegas | Celebrity Home Health';
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <JsonLd data={jsonLd as Record<string, unknown>} id="json-ld-home-health-aide" />
      <Header />
      <section className="relative pt-32 pb-20 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://public.readdy.ai/ai/img_res/aee696d8c1a2778a70eb0018bde7c99a.jpg" alt="Home Health Aide" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/85 to-slate-900/98"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 border border-amber-400/30 rounded-full text-amber-300 text-xs font-semibold uppercase tracking-wider mb-6"><i className="ri-user-heart-line"></i> Home Health Aide</div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">Compassionate Home<br />Health <span className="text-amber-400">Aide Services</span></h1>
            <p className="text-slate-300 text-xl leading-relaxed mb-8">Our certified home health aides provide warm, dignified personal care assistance — helping you or your loved one maintain comfort, independence, and quality of life at home in Las Vegas.</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="/contact" className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-amber-600 hover:bg-amber-500 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"><i className="ri-calendar-check-line"></i> Request an Aide</a>
              <a href="tel:866-939-0330" className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-white/10 hover:bg-white/15 border border-white/20 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"><i className="ri-phone-line"></i> 866-939-0330</a>
            </div>
          </div>
        </div>
      </section>
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <div className="inline-block px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">Personal Care Services</div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">What Our Aides Provide</h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto">Thoughtful, dignified assistance with everyday tasks — allowing you to live comfortably and safely at home.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: 'ri-drop-line', title: 'Bathing & Hygiene', desc: 'Gentle assistance with bathing, showering, oral care, hair care, and personal hygiene.' },
              { icon: 'ri-shirt-line', title: 'Dressing & Grooming', desc: 'Help with dressing, undressing, and grooming to maintain dignity and confidence.' },
              { icon: 'ri-restaurant-line', title: 'Meal Preparation', desc: 'Nutritious meal prep and feeding assistance tailored to dietary needs and preferences.' },
              { icon: 'ri-home-2-line', title: 'Light Housekeeping', desc: 'Tidying, laundry, dishwashing, and light cleaning to maintain a safe, clean environment.' },
              { icon: 'ri-capsule-line', title: 'Medication Reminders', desc: 'Timely prompts to take medications as prescribed — supporting adherence and safety.' },
              { icon: 'ri-heart-2-line', title: 'Companionship', desc: 'Warm conversation, emotional support, and social engagement to combat isolation.' },
            ].map((item, i) => (
              <div key={i} className="bg-stone-50 p-7 rounded-2xl border border-stone-200 hover:border-amber-300 transition-all duration-300">
                <div className="w-11 h-11 bg-amber-100 rounded-xl flex items-center justify-center mb-4"><i className={`${item.icon} text-amber-700 text-lg`}></i></div>
                <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-5">Care That Feels Like Family</h2>
          <p className="text-slate-400 text-lg mb-8">Contact Celebrity Home Health to arrange home health aide services in Las Vegas, Henderson, Summerlin, and surrounding areas.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-amber-600 hover:bg-amber-500 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"><i className="ri-calendar-check-line"></i> Request Care Now</a>
            <a href="/services" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/15 border border-white/20 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer">View All Services</a>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
